import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { CmsAuthService } from '../../cms/services/cms-auth.service';
import { TotaraAuthService } from '../../totara/services/totara-auth.service';
import { AuthTarget } from '../models/auth-target.model';
import { LoginResponse } from '../models/login-response.model';
import { AuthBase } from './auth-base';
import { AuthUnauthorized } from './auth-unauthorized';
import { LocalStorageKeys } from '@cms_root/app/core/models/local-storage-keys';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements AuthUnauthorized {
  private static _refreshing = false;

  public static get refreshing(): boolean {
    return this._refreshing;
  }

  public get isAuth$(): Observable<boolean> {
    return this.cmsAuthService.isAuth$;
  }

  public get isAuth(): boolean {
    return this.cmsAuthService.isAuth;
  }

  constructor(
    private readonly router: Router,
    private readonly cmsAuthService: CmsAuthService,
    private readonly totaraAuthService: TotaraAuthService
  ) {}

  public init(): void {
    this.cmsAuthService.init();
    this.totaraAuthService.init();
  }

  public loginUser(authSessionId: string, target: AuthTarget): void {
    AuthService._refreshing = true;

    this.getAuthService(target)
      .loginUser(authSessionId)
      .pipe(finalize(() => (AuthService._refreshing = false)))
      .subscribe((data: LoginResponse) => {
        const redirectURL = localStorage.getItem(LocalStorageKeys.authRedirect) ?? '/';
        this.router.navigateByUrl(redirectURL);
      });
  }

  public logoutUser(): void {
    this.cmsAuthService.logoutUser();
    this.totaraAuthService.logoutUser();
  }

  public refreshSession(url?: string, authTarget?: AuthTarget): void {
    if (AuthService.refreshing) {
      return;
    }

    AuthService._refreshing = true;
    const redirectUrl: string = url || this.router.url;

    this.getAuthService(authTarget).unauthorized(redirectUrl);
  }

  private getAuthService(target: AuthTarget): AuthBase {
    switch (target) {
      case AuthTarget.cms:
        return this.cmsAuthService;

      case AuthTarget.totara:
        return this.totaraAuthService;

      default:
        return this.cmsAuthService;
    }
  }
}
